import { ref, watch } from 'vue';

import { currentIdentity, sessionStatus } from '@/api/session';
import { fetchMyTickets } from '@/api/tickets';

/** Is a reply waiting in any of the visitor's threads — what the launcher's dot shows. */
export const hasUnreadReply = ref(false);

// Somebody else's dot must not outlive them on the launcher.
watch(currentIdentity, () => {
  hasUnreadReply.value = false;
});

/**
 * Ask whether a reply is waiting, for the visitor the frame serves right now.
 *
 * Asked ONLY once the visitor has opened the panel on this page, and that
 * limit is deliberate. Before the first open the frame holds no session — the
 * exchange is deferred so that a visitor who never clicks costs the contact API
 * nothing — and starting one here would turn every page view of every signed-in
 * person into an RSA verify and a contact upsert, against a limit of ten
 * exchanges a minute per address that a whole office shares. So a page load
 * that has not opened the widget shows no dot; the reply is still in the list
 * the moment they do, and in their inbox if they never do.
 *
 * The answer is kept only if it is still about the same person. The launcher
 * is one long-lived component — unlike the panel, it is not remounted when the
 * host signs somebody else in — so a request sent for visitor A can come back
 * after the frame has become visitor B's. Committing it would put A's dot on
 * B's launcher: one bit about another contact's threads, and one that stays,
 * because B has no session yet and nothing asks again until they open the
 * panel.
 */
export const refreshUnread = async (): Promise<void> => {
  if (sessionStatus.value !== 'ready') {
    return;
  }

  const askedFor = currentIdentity.value;
  const tickets = await fetchMyTickets();

  if (askedFor !== currentIdentity.value) {
    return;
  }

  hasUnreadReply.value = tickets.some((ticket) => ticket.unread);
};
